import { api } from "@/api/client";
import type { AuthMode } from "@/types/api";

export type OneCConfigType = "unf" | "bp";
export type OneCMetadataKey = "onec_unf" | "onec_bp" | "onec_bp_3";

/** Справочник баз 1С для SSO-режима. В SSO-сессии портал ходит в 1С по JWT в
 *  базу подключения пользователя, а подключение сопоставляется с базой
 *  справочника по адресу публикации (`aud` токена — из базы). Заводит админ. */
export interface OneCBase {
  id: string;
  name: string;
  /** Адрес публикации без `/odata/…` и `/hs/…`, напр. `https://1c.mpomega.ru/unf`. */
  base_url: string;
  /** `aud` токена — ровно как в `default.vrd` публикации. */
  audience: string;
  config_type: OneCConfigType;
  /** Ключ metadata учётки SSO с именем пользователя 1С этой базы. */
  metadata_key: OneCMetadataKey;
  created_at: string;
  updated_at: string;
}

/** Справочник + состояние ТЕКУЩЕЙ сессии. */
export interface OneCBaseList {
  bases: OneCBase[];
  auth_mode: AuthMode;
  /** Ключи metadata, для которых в SSO-сессии есть пользователь 1С. Для
   *  парольной сессии — []. */
  onec_metadata_keys: string[];
}

export interface OneCBaseCreate {
  name: string;
  base_url: string;
  audience: string;
  config_type: OneCConfigType;
  metadata_key: OneCMetadataKey;
}

/** config_type у заведённой базы не меняется. */
export interface OneCBaseUpdate {
  name?: string;
  base_url?: string;
  audience?: string;
  metadata_key?: OneCMetadataKey;
}

export const CONFIG_TYPE_TITLES: Record<OneCConfigType, string> = {
  bp: "БП",
  unf: "УНФ",
};

/** Какие ключи metadata SSO подходят конфигурации: пользователь 1С УНФ — в
 *  `onec_unf`, пользователи двух баз БП — в `onec_bp` и `onec_bp_3`. */
export const METADATA_KEYS_BY_CONFIG: Record<OneCConfigType, OneCMetadataKey[]> = {
  bp: ["onec_bp", "onec_bp_3"],
  unf: ["onec_unf"],
};

export const ONEC_BASES_QUERY_KEY = ["onec-bases"] as const;

export async function listOneCBases(): Promise<OneCBaseList> {
  const { data } = await api.get<OneCBaseList>("/onec-bases");
  return data;
}

export async function createOneCBase(body: OneCBaseCreate): Promise<OneCBase> {
  const { data } = await api.post<OneCBase>("/onec-bases", body);
  return data;
}

export async function updateOneCBase(id: string, body: OneCBaseUpdate): Promise<OneCBase> {
  const { data } = await api.put<OneCBase>(`/onec-bases/${id}`, body);
  return data;
}

export async function deleteOneCBase(id: string): Promise<void> {
  await api.delete(`/onec-bases/${id}`);
}
